import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import useInterval from '../../hooks/useInterval'

import {
  refreshCache,
  configureNewAppVersionAvailable,
  checkForNewAppVersion,
  APP_VALIDATION_INTERVAL,
} from '../../utils/newAppVersionAvailable'
import createDataDogLogNoHistory from '../../utils/datadog'

import useSpinner from '../../context/hooks/useSpinner'

import ConfirmModalV2 from '../../components/ConfirmModalV2'

import { getRemoteVersion } from '../../selectors/appState'
import { getNetworkAvailable } from '../../selectors/config'

import { MODES } from '../../VNMode/Reducer'
import { getDeviceMode } from '../../VNMode/Selectors'

import { CFD_SCREEN } from '../../VNCustomerFacingDisplay/Reducer'
import { getCFDScreen } from '../../VNCustomerFacingDisplay/Selectors'

import { getIsSyncing } from '../../VNSyncManager/Selectors'

import { getShouldUpdate, getShouldShowUpdateModal, getUpdateAvailable } from '../Selectors'

import { hasCartItems } from '../../selectors/cart'

import { setShouldUpdate } from '../ActionCreators'

const LOADING_UPDATE_TEXT = 'Updating version...'

// A module to manager web software updates.
export const VNUpdateManager = () => {
  const dispatch = useDispatch()

  const { startSpinner } = useSpinner()

  const cartHasItems = useSelector(hasCartItems)

  const shouldUpdate = useSelector(getShouldUpdate)
  const shouldShowUpdateModal = useSelector(getShouldShowUpdateModal)
  const updateAvailable = useSelector(getUpdateAvailable)
  const remoteVersion = useSelector(getRemoteVersion)
  const networkAvailable = useSelector(getNetworkAvailable)
  const cfdMode = useSelector(getDeviceMode)
  const cfdScreen = useSelector(getCFDScreen)
  const isSyncInProgress = useSelector(getIsSyncing)

  const [confirmUpdateNow, setConfirmUpdateNow] = useState(false)
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)

  const initiateUpdateNow = useCallback(() => {
    if (!networkAvailable) return
    if (!updateAvailable) return
    if (!shouldUpdate && !shouldShowUpdateModal) return

    createDataDogLogNoHistory('info', `An update is available!`, {
      networkAvailable,
      showConfirmationModal,
      shouldUpdate,
      shouldShowUpdateModal,
      updateAvailable,
      remoteVersion,
    })

    startSpinner(LOADING_UPDATE_TEXT)

    // Wait a moment to ensure spinner is visible.
    setTimeout(() => {
      refreshCache(cfdMode !== MODES.CFD)
    }, 2000)
  }, [
    startSpinner,
    remoteVersion,
    networkAvailable,
    shouldUpdate,
    shouldShowUpdateModal,
    updateAvailable,
    showConfirmationModal,
    cfdMode,
  ])

  useInterval(() => {
    setConfirmUpdateNow(true)
  }, APP_VALIDATION_INTERVAL)

  useEffect(() => {
    dispatch(setShouldUpdate(!cartHasItems && !isSyncInProgress))
  }, [dispatch, cartHasItems, isSyncInProgress])

  useEffect(() => {
    const intervalId = configureNewAppVersionAvailable()

    return () => {
      setConfirmUpdateNow(false)
      clearInterval(intervalId)

      createDataDogLogNoHistory('info', `Shutting down update manager!`, {
        intervalId,
      })
    }
  }, [])

  // Update immediately when immmediate update is confirmed.
  useEffect(() => {
    if (!confirmUpdateNow) return

    // Auto-update is disabled in CFD mode when not idle.
    if (cfdMode === MODES.CFD && cfdScreen !== CFD_SCREEN.IDLE) return

    if (shouldShowUpdateModal) {
      setConfirmUpdateNow(false)
      setShowConfirmationModal(networkAvailable && updateAvailable && !cartHasItems)
      return
    }

    initiateUpdateNow()
  }, [
    initiateUpdateNow,
    confirmUpdateNow,
    networkAvailable,
    shouldShowUpdateModal,
    updateAvailable,
    cartHasItems,
    cfdMode,
    cfdScreen,
  ])

  // Effect to check for new version when the update modal is allowed to be shown.
  useEffect(() => {
    if (!shouldShowUpdateModal) return
    if (!networkAvailable) return

    checkForNewAppVersion()
  }, [shouldShowUpdateModal, networkAvailable])

  // Show the confirmation modal immediately when the update modal is allowed.
  useEffect(() => {
    if (!shouldShowUpdateModal) return

    setShowConfirmationModal(networkAvailable && updateAvailable && !cartHasItems && !isSyncInProgress)
  }, [shouldShowUpdateModal, updateAvailable, networkAvailable, confirmUpdateNow, cartHasItems, isSyncInProgress])

  return (
    showConfirmationModal &&
    !confirmUpdateNow && (
      <ConfirmModalV2
        headerText="Version Update Available"
        subtext={'Software version ' + remoteVersion + ' is available. Tap OK to proceed with the update.'}
        buttonOneText="Cancel"
        buttonTwoText="OK"
        onButtonOneClick={() => {
          setShowConfirmationModal(false)
        }}
        onButtonTwoClick={() => {
          setShowConfirmationModal(false)
          initiateUpdateNow()
        }}
      />
    )
  )
}
